/*
 * The people directory (spec 12 §4.5): the member list every account can open, the profile card
 * behind a person, and the labels of the directory's groups section.
 *
 * Those three — `people.groups.title`, `.empty` (with its lead) and `.members` — stay here rather than
 * moving to the groups catalogue because the groups section *is* a section of this directory; the
 * screens that render them live in `core/groups/`, and the rest of their copy is that area's
 * `i18n/en.ts`.
 *
 * The status copy at the end is the same arrangement one step further: a status is set from the
 * account menu in `core/shell/` and drawn on half a dozen surfaces in this area and in the chat
 * module, and all of it is named here, because a status is a fact about a person and this is the
 * catalogue of the people area.
 */

export const en = {
	// The page is the directory and its two sections are people and groups, so the frame is named for
	// the whole of it and the sections keep their own words.
	"people.title": "Directory",
	// A lead says what the page is in as few words as the page needs (VISUAL.md §7.11). It is the whole
	// instance: an account is here from the moment it is created, and one whose invitation nobody has
	// answered yet wears the *Invited* pill rather than being left out of the list a mention is picked
	// from.
	"people.description": "Everyone on this instance.",
	"people.sections": "Directory sections",
	"people.empty": "Nobody here yet.",
	"people.empty.lead": "Accounts appear here as soon as somebody has enrolled.",
	// The one thing to do about an empty directory, offered to the only people who can do it — and the
	// same door from the toolbar, where it is one action among the list's own rather than the only one.
	"people.invite": "Invite the team",
	"people.invitePeople": "Invite people",
	"people.count": "{count} people",
	"people.count.one": "{count} person",
	// While something is typed the count says how much of the list is left, not how much there is.
	"people.countFiltered": "{shown} of {count} people",
	"people.countFiltered.one": "{shown} of {count} person",

	/** The directory's first section; the second one is `people.groups.title`. */
	"people.tab.people": "People",

	// Finding somebody.
	"people.filter.label": "Find a person",
	"people.filter.placeholder": "Name or handle",
	"people.noMatch": "Nobody matches that.",
	"people.clearFilter": "Clear the filter",

	// A card's second line: the handle, and what the person does wherever the server sent a title.
	// The time it is where they are is not on a card in a grid of ten — it is one fact per person the
	// eye would have to read ten of — but it is on the hover card, the profile and a direct
	// conversation's band, which are the three surfaces one person is looked at on.
	"people.card.meta": "@{handle}",
	"people.card.metaTitle": "@{handle} · {title}",

	// What a row says about an account.
	"people.column.name": "Name",
	"people.column.handle": "Handle",
	"people.column.access": "Access",
	"people.column.state": "State",
	"people.kind.bot": "Bot",
	// A person's standing (`standing.ts`): an administrator is in Administrators, a team member in
	// Team members, and a guest in neither.
	"people.standing.admin": "Admin",
	"people.standing.member": "Team member",
	"people.standing.guest": "Guest",
	// No word for a live account: a state cell that says *Active* on every row says "normal" on
	// every row and points at nothing, so the ordinary state is an empty cell and only what is not
	// ordinary is named (X-B2 B2-15, VISUAL.md §7.11).
	"people.state.deactivated": "Deactivated",

	// ---- E-01: what a member may now learn about a colleague ----
	//
	// An account whose invitation nobody has answered yet. It wears the warning pill wherever a
	// person is drawn, because a mention of somebody who cannot read it looks exactly like a mention
	// of somebody who can (X-B1 D8).
	"people.state.invited": "Invited",
	// The time it is where this person sits, on the two surfaces that have room for one line about
	// them: a direct conversation's band names the place as well — the band is about one person, and
	// "in Berlin" is the half of the sentence that explains the other half — while the hover card,
	// which is read beside the name it hangs off, says the clock alone. The city is the zone's last
	// segment with its underscores opened out; a zone with no segment (`UTC`) is named as it is.
	"people.time.in": "{time} in {city}",
	"people.time.local": "{time} local",

	// The profile card.
	"people.profile.open": "Profile of {name}",
	// Under the name: what this account is and what it is called, in one quiet line. `{access}` is the
	// standing's word, or *Bot*.
	"people.profile.subtitle": "{access} · @{handle}",
	"people.profile.fullName": "Full name",
	"people.profile.jobTitle": "Title",
	"people.profile.pronouns": "Pronouns",
	// The zone is shown as the time it is where the person is; the IANA name itself tells a reader
	// nothing, and only an admin is sent a zone at all (security.md §9.6).
	"people.profile.localTime": "Local time",
	"people.profile.email": "E-mail address",
	"people.profile.sharedChannels": "Shared channels",
	"people.profile.lastLogin": "Last signed in",
	"people.profile.copyEmail": "Copy e-mail",
	"people.profile.copied": "Copied.",
	"people.message": "Message",
	// On a card in a grid of ten the word alone is ten identical controls to a screen reader, so the
	// name is in the accessible one and the visible label stays the word (WCAG 2.5.3).
	"people.messageNamed": "Message {name}",

	// W-05 — a person is drawn one way (PersonChip.tsx): the hover card's own name and the two words
	// that are the accessible half of the presence dot.
	"people.card.label": "About {name}",
	"people.presence.away": "Away",
	"people.presence.online": "Online",

	// ---- D2: the status a person sets about themselves (status.ts, StatusDialog.tsx) ----
	//
	// The account menu's two rows. The row that opens the dialog names the status it would change
	// rather than drawing it: a menu label is a plain string, and an emoji that is not wrapped in
	// `.tb-emoji` comes out of the interface face as a tofu box (X-A1 D12). The emoji itself is drawn
	// in the block above the rows, where the markup is the menu's own.
	// No ellipsis, although this row opens a dialog: in this product an ellipsis means "something is
	// waiting", and every other dialog opener — *Rename channel*, *Forward*, *New channel* — reads
	// without one (`i18n/glossary.test.ts`, B2-24).
	"people.status.set": "Set a status",
	"people.status.change": "Status: {status}",
	"people.status.clear": "Clear status",
	// One key for two doors: the menu's checkbox row and the dialog's own switch are the same claim,
	// and they say the same four words.
	"people.status.showAway": "Show me as away",
	"people.status.showAway.hint":
		"Your dot stays away until you switch this off, whatever your connection says.",

	// The dialog.
	"people.status.title": "Set a status",
	"people.status.lead": "Everyone who looks you up sees this.",
	"people.status.presets": "Presets",
	"people.status.emoji": "Emoji",
	"people.status.noEmoji": "No emoji",
	"people.status.text": "Status",
	"people.status.text.hint": "At most {max} characters, and everyone on this instance can read it.",
	"people.status.clearAfter": "Clear after",
	"people.status.clearAfter.30m": "30 minutes",
	"people.status.clearAfter.1h": "1 hour",
	"people.status.clearAfter.4h": "4 hours",
	"people.status.clearAfter.today": "Today",
	"people.status.clearAfter.week": "This week",
	"people.status.clearAfter.never": "Never",
	// Offered only while the stored status already ends somewhere, so that a dialog opened to fix a
	// typo does not quietly move the moment it clears at. The moment is spelled the way every other
	// moment in the interface is (`formatDayTime`): the clock alone while it is today, the day in
	// front of it once it is not.
	"people.status.clearAfter.keep": "Leave it as it is ({when})",

	// What a preset writes into the line, which is also what its button says.
	"people.status.preset.meeting": "In a meeting",
	"people.status.preset.lunch": "Lunch",
	"people.status.preset.sick": "Off sick",
	"people.status.preset.holiday": "On holiday",
	"people.status.preset.commuting": "Commuting",
	"people.status.preset.focus": "Focusing",
	"people.status.preset.away": "Away",

	// How a status reads on the surfaces that draw one (`formatUntil`). The moment is the person's own
	// end time on the reader's clock: the hour alone while it is today, and the day with the hour on
	// it once it is not — "until 13:00" against "until 20 September 17:40", which is the shape every
	// other sentence in the interface that points at a moment already has.
	"people.status.until": "until {time}",
	"people.status.untilDate": "until {date}",
	// There is no separate word for the away *claim*: it is drawn on the presence dot and nowhere
	// else (`presence.ts`'s `withAway`), so the dot's own `people.presence.away` says it. A second
	// entry reading "Away" would be one word for one thing in two catalogue lines.

	// The palette, named for the reader who cannot see it. The list itself is the server's
	// (`wire.StatusEmojiPalette`); these are the names, and `status.test.ts` fails while one is
	// missing.
	"people.status.emoji.calendar": "Calendar",
	"people.status.emoji.meal": "Plate and cutlery",
	"people.status.emoji.sick": "Face with a thermometer",
	"people.status.emoji.palm": "Palm tree",
	"people.status.emoji.bus": "Bus",
	"people.status.emoji.sleep": "Sleeping",
	"people.status.emoji.headphones": "Headphones",
	"people.status.emoji.house": "House",
	"people.status.emoji.plane": "Aeroplane",
	"people.status.emoji.car": "Car",
	"people.status.emoji.baby": "Baby",
	"people.status.emoji.graduation": "Graduation cap",
	"people.status.emoji.coffee": "Hot drink",
	"people.status.emoji.running": "Running",
	"people.status.emoji.phone": "Telephone",
	"people.status.emoji.laptop": "Laptop",
	"people.status.emoji.bellOff": "Bell with a line through it",
	"people.status.emoji.party": "Party popper",
	"people.status.emoji.handshake": "Handshake",
	"people.status.emoji.writing": "Writing hand",
	"people.status.emoji.meditation": "Person meditating",
	"people.status.emoji.roadworks": "Roadworks",
	"people.status.emoji.moon": "Crescent moon",
	"people.status.emoji.hourglass": "Hourglass",

	// The groups section of the directory; its screens are core/groups.
	"people.groups.title": "Groups",
	"people.groups.empty": "There are no groups yet.",
	"people.groups.empty.lead": "A group is a handle that reaches several people at once.",
	"people.groups.members": "{count} members",
	"people.groups.members.one": "{count} member",

	// The typing line over a message box (typing.ts), in every room that has one: coalesced at two
	// names — beyond that it says how the room feels rather than who is in it, because a list of five
	// names over the box is a paragraph that moves.
	"people.typing.single": "{name} is typing …",
	"people.typing.pair": "{names} are typing …",
	"people.typing.several": "Several people are typing …",
} as const satisfies Record<string, string>
